import { Document, Scalar, visit } from 'yaml';
import type { CheckDefinition } from '@internal/backstage-plugin-pulse-common';
import { downloadYaml } from '../yaml-export';

// Runtime-only fields that may be attached to a check row (source flag, DB
// timestamps and the per-check compliance aggregate) but must never end up in
// exported config.
const RUNTIME_ONLY_FIELDS = [
  'source',
  'createdAt',
  'updatedAt',
  'aggregate',
  'checkId',
  'passedEntities',
  'totalEntities',
  'percentage',
];

// Manual checks are decided by a user, so the backend injects these on load
// rather than reading them from config.
const MANUAL_CHECK_OMITTED_FIELDS = [
  'pathResolver',
  'rule',
  'passedMessage',
  'failedMessage',
];

const toAuthoredCheck = (check: CheckDefinition): Record<string, unknown> => {
  const definition = { ...check } as Record<string, unknown>;
  const omitted =
    check.type === 'manual'
      ? [...RUNTIME_ONLY_FIELDS, ...MANUAL_CHECK_OMITTED_FIELDS]
      : RUNTIME_ONLY_FIELDS;
  for (const field of omitted) {
    delete definition[field];
  }
  return definition;
};

/**
 * Serialises check definitions to the same YAML list shape used by
 * `pulse/checks/*.yaml`, stripping runtime-only fields.
 */
export const checksToYaml = (checks: CheckDefinition[]): string => {
  const doc = new Document(checks.map(toAuthoredCheck));
  // Render long single-line values (e.g. JSONPath expressions) as folded block
  // scalars (>-) so they wrap at spaces instead of continuing a plain scalar,
  // matching the hand-authored pulse/checks/*.yaml style. Multi-line strings
  // keep their default literal (|-) block style.
  visit(doc, {
    Scalar(key, node) {
      if (
        key === 'value' &&
        typeof node.value === 'string' &&
        !node.value.includes('\n') &&
        node.value.length > 80
      ) {
        node.type = Scalar.BLOCK_FOLDED;
      }
    },
  });
  return doc.toString({ lineWidth: 80 });
};

/** Serialises a single check as a single-item YAML list. */
export const checkToYaml = (check: CheckDefinition): string =>
  checksToYaml([check]);

/** Triggers a browser download of the check definition as a YAML file. */
export const downloadCheckYaml = (check: CheckDefinition): void =>
  downloadYaml(`${check.id}.yaml`, checkToYaml(check));

/** Triggers a browser download of all given checks as one YAML file. */
export const downloadChecksYaml = (checks: CheckDefinition[]): void =>
  downloadYaml('checks.yaml', checksToYaml(checks));
